// @ts-nocheck
/*
 * Port of views/dashboard/incoming-summary/cdr-report.js (781L).
 *
 * ACL-gated CDR report across one or more DIDs + a date range (up to 6
 * months back). Summary cards over the loaded rows, 21 columns, an
 * expandable detail panel per row, and a 44-column CSV export. Caller /
 * DID / callee / agent numbers are masked via `maskNumberForHangup`.
 *
 * The 30s client-side refresh throttle (`loadPortalReportData`) is
 * deliberately disabled here, matching v1 — the request goes straight to
 * `getApiCall`.
 *
 * V1 QUIRK preserved: the first useEffect that reads `Storage.user` has
 * NO dependency array — runs on every render. Kept verbatim.
 */
import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import type { ColumnDef, Table as TanstackTable } from '@tanstack/react-table';
import { DataTable } from '@/components/data-table/DataTable';
import { Button } from '@/components/ui/button';
import { MultiSelect } from '@/components/ui/multi-select';
import { getApiCall } from '@/service/httpCall';
import Loader from '@/components/partials/components/Loader';
import { USER_ROLES } from '@/utilities/constant';
import { Storage, getUser } from '@/utilities/setting';
import { hasPermission } from '@/utilities/helpers';
import { toast } from 'sonner';
import { friendlyApiError } from '@/utilities/apiErrorMessage';
import moment from 'moment';
import {
  formatDispositionJson,
} from '@/features/ivr-reports/DispositionJsonCell';

import { CDR_REPORT, CDR_TRACE_BATCH_REPORT, API_SCOPED_NUMBERS_LITE, API_HANGUP_CAUSES, API_AGENTS_LIST } from '@/service/url';
import { maskNumber, maskNumberForHangup } from '@/utilities/cliDisplay';
import { isPseudoCdrRow, pseudoCdrLabel } from '@/utilities/pseudoCdr';
import PseudoCdrBadge from '@/components/partials/components/PseudoCdrBadge';
import { FillPage } from '@/components/layout/PageContainer';
import { PageHeader } from '@/components/feedback/PageHeader';
import BusyGuard from '@/components/feedback/BusyGuard';
import { Eye } from 'lucide-react';
import { parseModuleOperations } from '@/utilities/voicePlanAcl';
import { canReadRecordings } from '@/utilities/recordingsAcl';
import CallRecordingCell from '@/components/partials/components/CallRecordingCell';
import CdrCallDetailDialog from '@/features/ivr-reports/components/CdrCallDetailDialog';
import { exportTraceReport } from '@/features/ivr-reports/DidReportDetailPage';
import { CallEngineTracesButton } from '@/features/ivr-reports/components/CallEngineTraces';
import {
  CallTranscriptButton,
  useCanViewTranscripts,
  useTranscriptAvailability,
} from '@/features/transcripts/CallTranscript';
import { CalendarDateInput } from '@/components/ui/calendar-inputs';
import { callStatusLabel, isCallConnected } from '@/utilities/callStatus';
import { stampedFileName } from '@/components/data-table/csv-export';

// Backend getCDR rejects more (cdr-reports-controller.js).
const MAX_CDR_DIDS = 20;

export const formatDateTime = (dateString: any) => {
  if (!dateString) return '';
  const m = moment(String(dateString), ['YYYY-MM-DD HH:mm:ss', moment.ISO_8601], true);
  if (!m.isValid()) return '';
  return m.format('YYYY-MM-DD HH:mm:ss');
};

const getDefaultDates = () => {
  const now = new Date();
  const start = new Date(now.getFullYear(), now.getMonth(), 1);
  const end = new Date();
  return { start, end };
};

const dateToInputStr = (d: any) => {
  if (!d) return '';
  const dt = d instanceof Date ? d : new Date(d);
  if (Number.isNaN(dt.getTime())) return '';
  const y = dt.getFullYear();
  const m = String(dt.getMonth() + 1).padStart(2, '0');
  const day = String(dt.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

const inputStrToLocalDate = (str: any) => {
  if (!str) return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(str);
  if (!m) {
    const d = new Date(str);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  return new Date(
    parseInt(m[1], 10),
    parseInt(m[2], 10) - 1,
    parseInt(m[3], 10),
  );
};

const lastTenDigits = (value: any) => {
  const digits = String(value ?? '').replace(/\D/g, '');
  if (!digits) return '';
  return digits.length > 10 ? digits.slice(-10) : digits;
};

const numbersMatch = (a: any, b: any) => {
  const left = lastTenDigits(a);
  const right = lastTenDigits(b);
  return Boolean(left && right && left === right);
};

// Masking agents get caller_number masked but did plain, so also compare the masked form.
const customerNumber = (row: any) =>
  row.call_type === 'outgoing' &&
  (numbersMatch(row.caller_number, row.did) ||
    (row.did != null && String(row.caller_number ?? '') === maskNumber(String(row.did))))
    ? row.callee_number
    : row.caller_number;

const isCliCandidateNumber = (n: any) => {
  const type = String(n?.number_type || '').trim().toUpperCase();
  const routing = String(n?.number_routing_type || '').trim().toLowerCase();
  if (type === 'CLI') return true;
  if (type === 'DID' && (!routing || routing === 'outgoing' || routing === 'both')) return true;
  return false;
};

const callTypeBadge = (rowData: any) => {
  const isIncoming = rowData.call_type === 'incoming';
  return (
    <span
      className={
        'inline-flex items-center rounded-full px-2.5 py-0.5 text-[11px] font-semibold ring-1 ring-inset ' +
        (isIncoming
          ? 'bg-emerald-100 text-emerald-700 ring-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-300'
          : 'bg-violet-100 text-violet-700 ring-violet-200 dark:bg-violet-950/40 dark:text-violet-300')
      }
    >
      {isIncoming ? 'Incoming' : 'Outgoing'}
    </span>
  );
};

const num = (value: any) => {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
};

const formatSecs = (value: any) => {
  const total = num(value);
  if (total <= 0) return '0s';
  const mins = Math.floor(total / 60);
  const secs = total % 60;
  return mins > 0 ? `${mins}m ${secs}s` : `${secs}s`;
};

// Stored CDR call_status wins; older rows without it keep the billing / answer_status rule.
const legacyConnected = (row: any) =>
  num(row?.billing_duration) > 0 ||
  String(row?.answer_status || '').toLowerCase() === 'answered';
const isConnected = (row: any) => isCallConnected(row, legacyConnected(row));
const statusText = (row: any) => callStatusLabel(row, legacyConnected(row));

const statusPill = (row: any) => {
  const ok = isConnected(row);
  return (
    <span
      className={
        'inline-flex items-center whitespace-nowrap rounded-full px-2.5 py-0.5 text-[11px] font-bold uppercase ring-1 ring-inset ' +
        (ok
          ? 'bg-emerald-100 text-emerald-700 ring-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-300'
          : 'bg-rose-100 text-rose-700 ring-rose-200 dark:bg-rose-950/40 dark:text-rose-300')
      }
    >
      {statusText(row)}
    </span>
  );
};

const twoLineCell = (primary: any, secondary?: any) => (
  <span className="flex flex-col leading-tight">
    <span className="font-semibold">{primary || '-'}</span>
    {secondary ? (
      <span className="text-[11px] text-muted-foreground">{secondary}</span>
    ) : null}
  </span>
);

/** Call duration = stored total_duration; rows stored before it existed fall back to end - start. */
const totalDuration = (row: any) => {
  if (row?.total_duration != null && row.total_duration !== '') return num(row.total_duration);
  const start = row?.start_time
    ? new Date(String(row.start_time).replace(' ', 'T') + 'Z')
    : null;
  const end = row?.end_time
    ? new Date(String(row.end_time).replace(' ', 'T') + 'Z')
    : null;
  if (!start || !end || Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
    return num(row?.billing_duration) + num(row?.patch_duration);
  }
  return Math.max(0, Math.round((end.getTime() - start.getTime()) / 1000));
};

const CDR_SUMMARY_CARDS = [
  { key: 'total', label: 'Total Calls', className: 'text-blue-600 dark:text-blue-400' },
  { key: 'connected', label: 'Connected', className: 'text-emerald-600 dark:text-emerald-400' },
  { key: 'failed', label: 'Failed', className: 'text-rose-600 dark:text-rose-400' },
  // { key: 'incoming', label: 'Incoming Legs', className: 'text-sky-600 dark:text-sky-400' },
  // { key: 'outgoing', label: 'Outgoing Legs', className: 'text-orange-600 dark:text-orange-400' },
  // {
  //   key: 'talkTime',
  //   label: 'Total Talk Time',
  //   className: 'text-violet-600 dark:text-violet-400',
  //   isDuration: true,
  // },
  // {
  //   key: 'avgTalkTime',
  //   label: 'Avg Talk Time',
  //   className: 'text-teal-600 dark:text-teal-400',
  //   isDuration: true,
  // },
  { key: 'pseudo', label: 'Pseudo CDRs', className: 'text-yellow-600 dark:text-yellow-400' },
];

const buildCdrStats = (rows: any[] = []) => {
  const connected = rows.filter(isConnected).length;
  const talkTime = rows.reduce((sum, r) => sum + num(r.patch_duration), 0);
  return {
    total: rows.length,
    connected,
    failed: rows.length - connected,
    incoming: rows.filter((r) => r.call_type === 'incoming').length,
    outgoing: rows.filter((r) => r.call_type === 'outgoing').length,
    talkTime,
    avgTalkTime: connected > 0 ? Math.round(talkTime / connected) : 0,
    pseudo: rows.filter(isPseudoCdrRow).length,
  };
};

const CdrReport = () => {
  const [cdrData, setCdrData] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const { start, end } = getDefaultDates();
  const [startDate, setStartDate] = useState<any>(start);
  const [endDate, setEndDate] = useState<any>(end);
  const [selectedDids, setSelectedDids] = useState<string[]>([]);
  const [didOptions, setDidOptions] = useState<any[]>([]);
  const [cliOptions, setCliOptions] = useState<any[]>([]);
  const [selectedClis, setSelectedClis] = useState<string[]>([]);
  const [agentOptions, setAgentOptions] = useState<any[]>([]);
  const [selectedAgents, setSelectedAgents] = useState<string[]>([]);
  const [callDirection, setCallDirection] = useState('');
  const [didError, setDidError] = useState('');
  const [callIdSearch, setCallIdSearch] = useState('');
  const initialFetchDone = useRef(false);
  const [loggedInUserId, setLoggedInUserId] = useState<any>(null);
  const [loggedInUserRole, setLoggedInUserRole] = useState<any>(null);
  const [
    moduleOperationsForReadPermissionCheck,
    setModuleOperationsForReadPermissionCheck,
  ] = useState<any>({});
  const [hangupCauseMap, setHangupCauseMap] = useState<any>({});
  const [detailRow, setDetailRow] = useState<any>(null);
  const canViewRecordings = canReadRecordings();
  const canViewTranscripts = useCanViewTranscripts();

  const filteredCdrData = useMemo(() => {
    return cdrData.filter((row) => {
      if (callDirection && row.call_type !== callDirection) return false;
      if (selectedClis.length) {
        const cliHit = selectedClis.some((cli) => numbersMatch(row.caller_number, cli));
        if (!cliHit) return false;
      }
      if (selectedAgents.length) {
        const agentHit = selectedAgents.some((key) => {
          let username = '';
          let agentNumber = '';
          let agentName = '';
          try {
            const parsed = JSON.parse(key);
            username = String(parsed?.username || '');
            agentNumber = String(parsed?.agentNumber || '');
            agentName = String(parsed?.agentName || '');
          } catch {
            return false;
          }
          if (username && String(row.username || '').toLowerCase() === username.toLowerCase()) {
            return true;
          }
          if (agentName && String(row.agent_name || '').toLowerCase() === agentName.toLowerCase()) {
            return true;
          }
          if (agentNumber && numbersMatch(row.agent_number, agentNumber)) return true;
          return false;
        });
        if (!agentHit) return false;
      }
      return true;
    });
  }, [cdrData, callDirection, selectedClis, selectedAgents]);

  const stats = useMemo(() => buildCdrStats(filteredCdrData), [filteredCdrData]);

  const transcriptLineCount = useTranscriptAvailability(
    useMemo(() => filteredCdrData.map((r: any) => r?.trace_id), [filteredCdrData])
  );

  useEffect(() => {
    let cancelled = false;
    getApiCall(API_HANGUP_CAUSES)
      .then((resp: any) => {
        const list = Array.isArray(resp) ? resp : resp?.data || [];
        if (cancelled) return;
        setHangupCauseMap(
          Object.fromEntries(
            list.map((item: any) => [
              item.hangup_cause_code,
              item.message || item.hangup_cause_reason || '',
            ]),
          ),
        );
      })
      .catch(() => setHangupCauseMap({}));
    return () => {
      cancelled = true;
    };
  }, []);

  const hangupLabel = useCallback(
    (code: any) => (code ? hangupCauseMap[code] || String(code) : '-'),
    [hangupCauseMap],
  );

  useEffect(() => {
    const fetchUserData = async () => {
      const rawUser = Storage.getItem('user');
      if (!rawUser) return;
      try {
        const user = JSON.parse(rawUser);
        if (user) {
          setLoggedInUserId(user?.id);
          setLoggedInUserRole(user?.role);
        }
      } catch (e) {
        console.error('Invalid JSON in Storage user:', e);
      }
    };
    fetchUserData();
  });

  useEffect(() => {
    const user = getUser();
    if (user) {
      if (user?.acl_settings) {
        setModuleOperationsForReadPermissionCheck(
          parseModuleOperations(user.acl_settings.module_operations),
        );
      }
    }
  }, []);

  useEffect(() => {
    const onAclRefreshed = () => {
      const user = getUser();
      if (user?.acl_settings?.module_operations) {
        try {
          const parsed =
            typeof user.acl_settings.module_operations === 'string'
              ? parseModuleOperations(user.acl_settings.module_operations)
              : user.acl_settings.module_operations;
          setModuleOperationsForReadPermissionCheck(parsed);
        } catch (e) {
          console.log('Failed to apply refreshed ACL', e);
        }
      }
    };
    window.addEventListener('acl-refreshed', onAclRefreshed);
    return () => window.removeEventListener('acl-refreshed', onAclRefreshed);
  }, []);

  useEffect(() => {
    getApiCall(API_SCOPED_NUMBERS_LITE)
      .then((response: any) => {
        const list = Array.isArray(response) ? response : response?.data || [];
        const didNumbers = list
          .filter((n: any) => n?.number_type === 'DID' && n?.actual_number)
          .map((n: any) => String(n.actual_number).trim())
          .filter((n: string) => n !== '');
        const uniqueDids = [...new Set(didNumbers)].map((n) => ({ label: n, value: n }));
        setDidOptions(uniqueDids);
        setSelectedDids((prev) =>
          prev.length ? prev : uniqueDids.slice(0, 1).map((o) => o.value),
        );

        const cliNumbers = list
          .filter((n: any) => isCliCandidateNumber(n) && n?.actual_number)
          .map((n: any) => String(n.actual_number).trim())
          .filter((n: string) => n !== '');
        const uniqueClis = [...new Set(cliNumbers.length ? cliNumbers : didNumbers)].map((n) => ({
          label: n,
          value: n,
        }));
        setCliOptions(uniqueClis);
      })
      .catch(() => {
        setDidOptions([]);
        setCliOptions([]);
      });
  }, []);

  useEffect(() => {
    getApiCall(API_AGENTS_LIST)
      .then((response: any) => {
        const list = Array.isArray(response) ? response : [];
        const options = list
          .map((agent: any) => {
            const username = String(agent?.username || '').trim();
            const agentNumber = String(agent?.agent_number || '').trim();
            const agentName = String(agent?.agent_name || '').trim();
            if (!username && !agentNumber && !agentName) return null;
            const value = JSON.stringify({ username, agentNumber, agentName });
            const label = agentName || username || 'Agent';
            return { value, label };
          })
          .filter(Boolean);
        setAgentOptions(options);
      })
      .catch(() => setAgentOptions([]));
  }, []);

  const validateDids = (values: any) => {
    const list = (Array.isArray(values) ? values : [values])
      .map((v: any) => String(v ?? '').trim())
      .filter(Boolean);

    if (list.length === 0) {
      setDidError('Select at least one DID.');
      return null;
    }

    if (list.length > MAX_CDR_DIDS) {
      setDidError(`Select up to ${MAX_CDR_DIDS} DIDs.`);
      return null;
    }

    const invalid = list.find((v: string) => !/^\d{10,15}$/.test(v));
    if (invalid) {
      setDidError(`Invalid DID "${invalid}". Must be numeric, 10-15 digits.`);
      return null;
    }

    setDidError('');
    return list;
  };

  const fetchCdrReport = async (dids: any, start: any, end: any, force = false, callIdSearchValue = '') => {
    const didList = validateDids(dids);
    if (!didList) return;
    if (!loggedInUserId || !loggedInUserRole) {
      return;
    }

    setLoading(true);
    const startParam = start || moment().startOf('month').format('YYYY-MM-DD');
    const endParam = end || moment().format('YYYY-MM-DD');
    const didValue = didList.join(',');
    const idSearch = (callIdSearchValue || '').trim();

    try {
      const result: any = await getApiCall(
        `${CDR_REPORT}?did=${didValue}&startDate=${startParam}&endDate=${endParam}${
          idSearch ? `&callIdSearch=${encodeURIComponent(idSearch)}` : ''
        }`,
      );
      const list = Array.isArray(result) ? result : result?.data || [];
      setCdrData(list.map((val: any, idx: number) => ({ ...val, key: idx + 1 })));
    } catch (error: any) {
      toast.error(friendlyApiError(error) || 'Failed to fetch CDR report');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (initialFetchDone.current) return;
    if (!selectedDids.length || !loggedInUserId || !loggedInUserRole) return;
    initialFetchDone.current = true;
    const start = startDate ? moment(startDate).format('YYYY-MM-DD') : null;
    const end = endDate ? moment(endDate).format('YYYY-MM-DD') : null;
    fetchCdrReport(selectedDids, start, end);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDids, loggedInUserId, loggedInUserRole]);

  const handleSearch = () => {
    const start = startDate ? moment(startDate).format('YYYY-MM-DD') : null;
    const end = endDate ? moment(endDate).format('YYYY-MM-DD') : null;
    fetchCdrReport(selectedDids, start, end, true, callIdSearch);
  };

  const handleReset = () => {
    const { start, end } = getDefaultDates();
    setStartDate(start);
    setEndDate(end);
    setSelectedDids([]);
    setSelectedClis([]);
    setSelectedAgents([]);
    setCallDirection('');
    setDidError('');
    setCallIdSearch('');
    setCdrData([]);
  };

  const [exporting, setExporting] = useState(false);
  const exportingRef = useRef(false);
  // Same Calls / Call Legs / Recordings workbook as the DID detail export.
  const exportCdrReport = async (table: TanstackTable<any>) => {
    if (exportingRef.current) {
      toast.info('Export already in progress');
      return;
    }
    // One row per call: the table lists legs, the workbook lists calls (A_PARTY leg preferred).
    const byTrace = new Map<string, any>();
    const rows: any[] = [];
    table.getPrePaginationRowModel().rows.forEach(({ original: r }) => {
      if (!r?.trace_id) return void rows.push(r);
      const seen = byTrace.get(r.trace_id);
      if (!seen) {
        byTrace.set(r.trace_id, r);
        rows.push(r);
      } else if (String(r.call_leg || '').toUpperCase() === 'A_PARTY' && String(seen.call_leg || '').toUpperCase() !== 'A_PARTY') {
        rows[rows.indexOf(seen)] = r;
        byTrace.set(r.trace_id, r);
      }
    });
    if (!rows.length) {
      toast.info('No calls to export');
      return;
    }
    exportingRef.current = true;
    setExporting(true);
    const isOutgoing = (r: any) => r.call_type === 'outgoing';
    try {
      await exportTraceReport({
        rows,
        batchUrl: CDR_TRACE_BATCH_REPORT,
        // Same DID the detail dialog resolves for the row (row.did may be masked).
        didFor: (r) => selectedDids.find((d) => numbersMatch(d, r.did)) || r.did || selectedDids[0] || '',
        sourceFor: (r) => (isOutgoing(r) ? 'outbound' : 'incoming'),
        isOutgoingFor: isOutgoing,
        userRole: loggedInUserRole,
        hangupCauseMap,
        canViewRecordings,
        fileName: stampedFileName('cdr', 'xlsx'),
        hideUsername: true,
      });
    } finally {
      exportingRef.current = false;
      setExporting(false);
    }
  };

  const canRead = hasPermission(
    moduleOperationsForReadPermissionCheck,
    'CDR Report',
    'read',
  );

  if (
    Object.keys(moduleOperationsForReadPermissionCheck).length > 0 &&
    !canRead
  ) {
    return (
      <div
        className="text-center p-4"
        style={{
          marginTop: '50px',
          marginBottom: '50px',
          fontSize: '16px',
        }}
      >
        No read permission found in assigned ACL
      </div>
    );
  }

  const exportGuard = (
    <BusyGuard
      active={exporting}
      title="Please wait, your export is in progress"
      message="We're preparing your report. Leaving, refreshing or closing this page right now would cancel the export. You can continue as soon as your download starts. Thank you for your patience."
    />
  );

  if (loading) return <>{exportGuard}<Loader /></>;

  const aPartyByTrace = new Map<string, any>();
  cdrData.forEach((r: any) => {
    if (r?.trace_id && String(r.call_leg || '').toUpperCase() === 'A_PARTY') aPartyByTrace.set(r.trace_id, r);
  });
  const aPartyOf = (row: any) => aPartyByTrace.get(row?.trace_id) || row;
  const labelLine = (label: string, value: any) => (
    <span className="whitespace-nowrap">
      <span className="text-muted-foreground">{label}: </span>
      {value}
    </span>
  );

  const columns: ColumnDef<any>[] = [
    {
      id: 'details',
      header: 'Details',
      enableSorting: false,
      size: 80,
      cell: ({ row }: any) => (
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="h-8 gap-1 px-2"
          onClick={() => setDetailRow(row.original)}
        >
          <Eye size={14} /> View
        </Button>
      ),
    },
    {
      accessorKey: 'call_type',
      header: 'Call Type',
      cell: ({ row }: any) => callTypeBadge(row.original),
    },
    {
      id: 'status',
      header: 'Call Status',
      size: 200,
      accessorFn: (row) =>
        [statusText(row), hangupLabel(row.hangup_cause), row.call_failure_reason]
          .filter(Boolean)
          .join(' '),
      cell: ({ row }: any) => (
        <span className="flex flex-col items-start gap-0.5 text-xs leading-tight">
          {statusPill(row.original)}
          {hangupLabel(row.original.hangup_cause) ? <span>{hangupLabel(row.original.hangup_cause)}</span> : null}
          {row.original.call_failure_reason ? (
            <span className="font-medium text-rose-600 dark:text-rose-400">{row.original.call_failure_reason}</span>
          ) : null}
        </span>
      ),
    },
    {
      id: 'customer_number',
      header: 'Customer',
      accessorFn: (row) => customerNumber(row) ?? '',
      cell: ({ row }: any) =>
        maskNumberForHangup(customerNumber(row.original) ?? '-', row.original.hangup_cause),
    },
    {
      accessorKey: 'did',
      header: 'DID',
      cell: ({ row }: any) =>
        maskNumberForHangup(row.original.did ?? '-', row.original.hangup_cause),
    },
    {
      id: 'agent',
      header: 'Agent',
      size: 150,
      accessorFn: (row) => `${row.agent_name ?? ''} ${row.agent_number ?? ''}`.trim(),
      cell: ({ row }: any) =>
        twoLineCell(
          row.original.agent_name,
          maskNumberForHangup(row.original.agent_number ?? '', row.original.hangup_cause),
        ),
    },
    {
      id: 'callGroupIvr',
      header: 'Call Group / IVR',
      size: 160,
      accessorFn: (row) =>
        row.queue_name || row.call_group_name || row.ivr_name || '',
      cell: ({ row }: any) => {
        const r = row.original;
        const group = r.queue_name || r.call_group_name;
        return twoLineCell(group || r.ivr_name, group ? 'Call Group' : r.ivr_name ? 'IVR' : null);
      },
    },
    {
      id: 'callTimings',
      header: 'Call Timings',
      size: 190,
      accessorFn: (row) => aPartyOf(row).start_time || '',
      cell: ({ row }: any) => {
        const a = aPartyOf(row.original);
        return (
          <span className="flex flex-col text-xs leading-tight">
            {labelLine('Start', formatDateTime(a.start_time))}
            {labelLine('End', formatDateTime(a.end_time))}
          </span>
        );
      },
    },
    {
      id: 'callDuration',
      header: 'Call Duration',
      size: 150,
      accessorFn: (row) => totalDuration(aPartyOf(row)),
      cell: ({ row }: any) => {
        const a = aPartyOf(row.original);
        return (
          <span className="flex flex-col text-xs leading-tight">
            {labelLine('Total', `${totalDuration(a)}s`)}
            {labelLine('Talk Time', `${num(a.patch_duration)}s`)}
            {labelLine('Ring', `${num(a.bridge_ring_duration)}s`)}
            {labelLine('Hold', `${num(a.total_hold_duration ?? a.hold_duration)}s`)}
            {labelLine('Mute', `${num(a.mute_duration)}s`)}
          </span>
        );
      },
    },
    {
      id: 'pseudo',
      header: 'Pseudo CDR',
      accessorFn: (row) => pseudoCdrLabel(row),
      cell: ({ row }: any) => <PseudoCdrBadge row={row.original} />,
    },
    {
      id: 'campaign',
      header: 'Campaign',
      accessorFn: (row) => (row.campaign_id ? `#${row.campaign_id}` : ''),
      cell: ({ row }: any) =>
        row.original.campaign_id ? `#${row.original.campaign_id}` : '-',
    },
    // Disposition lives in the call detail modal only.
    {
      accessorKey: 'username',
      header: 'Username',
      cell: ({ row }: any) => row.original.username || '-',
    },
    { accessorKey: 'trace_id', header: 'Trace ID', size: 170 },
    {
      id: 'circleOperator',
      header: 'Circle & Operator',
      size: 170,
      accessorFn: (row) =>
        [row.circle_series_name, row.circle_series, row.circle_series_operator_name].filter(Boolean).join(' '),
      cell: ({ row }: any) => {
        const r = row.original;
        return twoLineCell(
          r.circle_series_name ? `${r.circle_series_name}${r.circle_series ? ` (${r.circle_series})` : ''}` : r.circle_series,
          r.circle_series_operator_name,
        );
      },
    },
    ...(canViewTranscripts
      ? [
          {
            id: 'transcript',
            header: 'Transcript',
            enableSorting: false,
            size: 90,
            cell: ({ row }: any) => (
              <CallTranscriptButton
                traceId={row.original.trace_id}
                lineCount={transcriptLineCount(row.original.trace_id)}
                className="h-8 w-8"
              />
            ),
          },
        ]
      : []),
    ...(canViewRecordings
      ? [
          {
            id: 'recording',
            header: 'Recording',
            enableSorting: false,
            cell: ({ row }: any) => <CallRecordingCell recordingFile={row.original.recording_file} />,
          },
        ]
      : []),
    ...(loggedInUserRole === USER_ROLES.SUPERADMIN
      ? [
          {
            id: 'engine_traces',
            header: 'Engine traces',
            enableSorting: false,
            size: 90,
            cell: ({ row }: any) => (
              <CallEngineTracesButton
                callSid={row.original.call_sid}
                sipCallId={row.original.sip_call_id}
                className="h-8 w-8"
              />
            ),
          },
        ]
      : []),
  ];

  return (
    <FillPage>
      {exportGuard}
      <PageHeader
        title="Call Detail Report"
        description="CDR lookup across one or more DIDs — every leg, its timing and hangup cause for the selected date range."
      />

      <div className="mt-4">
        <div className="mb-3 flex flex-wrap items-end gap-2">
          {/* Own row so the selected DIDs stay readable; other filters wrap below. */}
          <div className="flex w-full flex-col gap-1">
            <label className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
              DID(s) <span className="text-destructive">*</span>
            </label>
            <MultiSelect
              options={didOptions}
              value={selectedDids}
              onChange={(next) => {
                if (next.length > MAX_CDR_DIDS) {
                  toast.info(`You can select up to ${MAX_CDR_DIDS} DIDs at a time.`);
                  return;
                }
                setSelectedDids(next);
                if (didError) validateDids(next);
              }}
              placeholder="Select DID(s)"
              searchPlaceholder="Search DID…"
              emptyMessage="No DIDs available"
              maxChips={10}
              className={
                'w-full max-w-4xl ' + (didError ? 'ring-1 ring-destructive' : '')
              }
            />
            {didError && (
              <span className="text-xs text-destructive">{didError}</span>
            )}
          </div>

          <div className="flex flex-col gap-1">
            <label className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
              CLI
            </label>
            <MultiSelect
              options={cliOptions}
              value={selectedClis}
              onChange={setSelectedClis}
              placeholder="All CLIs"
              searchPlaceholder="Search CLI…"
              emptyMessage="No CLIs available"
              maxChips={2}
              className="w-[240px]"
            />
          </div>

          <div className="flex flex-col gap-1">
            <label className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
              Agent
            </label>
            <MultiSelect
              options={agentOptions}
              value={selectedAgents}
              onChange={setSelectedAgents}
              placeholder="All agents"
              searchPlaceholder="Search agent…"
              emptyMessage="No agents available"
              maxChips={2}
              className="w-[260px]"
            />
          </div>

          <div className="flex flex-col gap-1">
            <label className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
              Direction
            </label>
            <select
              value={callDirection}
              onChange={(e) => setCallDirection(e.target.value)}
              className="input-embossed h-10 w-[160px] rounded-md px-3 text-sm"
            >
              <option value="">All</option>
              <option value="incoming">Inbound</option>
              <option value="outgoing">Outbound</option>
            </select>
          </div>

          <div className="flex flex-col gap-1">
            <label className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
              From
            </label>
            <CalendarDateInput
              value={dateToInputStr(startDate)}
              onChange={(e) => setStartDate(inputStrToLocalDate(e))}
              min={dateToInputStr(moment().subtract(6, 'months').toDate())}
              max={dateToInputStr(new Date())}
              className="input-embossed h-10 w-[180px] rounded-md px-3 text-sm"
            />
          </div>

          <div className="flex flex-col gap-1">
            <label className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
              To
            </label>
            <CalendarDateInput
              value={dateToInputStr(endDate)}
              onChange={(e) => setEndDate(inputStrToLocalDate(e))}
              min={dateToInputStr(startDate)}
              max={dateToInputStr(new Date())}
              className="input-embossed h-10 w-[180px] rounded-md px-3 text-sm"
            />
          </div>

          <div className="flex flex-col gap-1">
            <label className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
              Call ID / Trace ID / SIP Call ID
            </label>
            <input
              type="text"
              value={callIdSearch}
              onChange={(e) => setCallIdSearch(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleSearch();
              }}
              placeholder="Search by call_sid, trace_id, sip_call_id"
              className="input-embossed h-10 w-[260px] rounded-md px-3 text-sm"
            />
          </div>

          <Button size="sm" onClick={handleSearch}>
            Search
          </Button>
          <Button variant="ghost" size="sm" onClick={handleReset}>
            Clear
          </Button>
        </div>

        {cdrData.length > 0 && (
          <div className="mb-4 grid gap-2.5 [grid-template-columns:repeat(auto-fit,minmax(150px,1fr))]">
            {CDR_SUMMARY_CARDS.map((card) => (
              <div key={card.key} className="rounded-lg border border-border/60 px-3 py-2.5">
                <div className="text-[10px] font-bold uppercase text-muted-foreground">
                  {card.label}
                </div>
                <div className={'text-xl font-bold ' + card.className}>
                  {card.isDuration ? formatSecs(stats[card.key]) : stats[card.key]}
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="scroll-container">
          <DataTable
            tableId="cdr-report:v3"
            columns={columns}
            data={filteredCdrData}
            pageSize={10}
            emptyTitle={
              !selectedDids.length
                ? 'Select one or more DIDs and press Search to load records.'
                : cdrData.length && !filteredCdrData.length
                  ? 'No records match the CLI, agent, or direction filters.'
                  : 'No records found.'
            }
            {...(loggedInUserRole === USER_ROLES.CALL_CENTRE_AGENT
              ? { disableExport: true }
              : { onExport: exportCdrReport })}
          />
        </div>
        <CdrCallDetailDialog
          row={detailRow}
          did={
            (detailRow &&
              (selectedDids.find((d) => numbersMatch(d, detailRow.did)) || detailRow.did || selectedDids[0])) ||
            ''
          }
          pageRows={cdrData}
          userRole={loggedInUserRole}
          hangupCauseMap={hangupCauseMap}
          transcriptLines={detailRow ? transcriptLineCount(detailRow.trace_id) : 0}
          onClose={() => setDetailRow(null)}
        />
      </div>
    </FillPage>
  );
};

export default CdrReport;
